/**
 * BackgroundUploadProcessor
 *
 * A render-nothing component mounted at the app level that silently processes
 * pending Imagine-AI upload jobs reported by the server.
 *
 * Flow:
 *  1. Poll  GET  /visual_scale/generations/api/v2/upload-job-status/  every 10 min
 *     (and immediately on mount once the user is available).
 *  2. For each pending item:
 *     - "output" → call bulk-create with the server-provided payload, then
 *       upload the s3_url file (+ compressed thumbnail) to the returned
 *       presigned URLs.
 *     - "link"   → call bulk-create with the linking payload (no file upload).
 *  3. Notify the server about processed tracking ids via
 *     POST /file-uploaded/ (success) or DELETE /file-uploaded/ (failed).
 *
 * No toasts or error messages are surfaced — this runs entirely in the
 * background. Survives route changes / tab switches because it is mounted
 * at the App level.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useUserContext } from '../contexts/UserContext';
import {
  bypassFailedUploadEntries,
  getUploadJobStatus,
  notifyFilesUploaded,
  postBulkCreateImagineAsset,
  postFileToPresignedData,
  triggerUpdateOutputCurator,
} from '../api/imagineai/backgroundUpload';
import {
  PRODUCT_SWAP_TOOL_TYPE,
  buildBackgroundUploadFormData,
  fetchImageUrlAsBlob,
  getFileExtensionForToolType,
  isFailedUploadStatus,
  replaceFileExtension,
} from '../helpers/backgroundUploadHelper';
import { get3DModelExtension } from '../helpers/jsHelper';
import { IMAGINE_AI_ROUTE_V2 } from '../constants/routePathConstants';
import {
  selectTriggerBackgroundUploadProcess,
  setTriggerBackgroundUploadProcess,
} from '../redux/slicers/uiSlicer';

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export default function BackgroundUploadProcessor() {
  const { currentUser } = useUserContext();
  const location = useLocation();
  const dispatch = useDispatch();
  const triggerBackgroundUploadProcess = useSelector(
    selectTriggerBackgroundUploadProcess
  );

  const isImagineAiRoute = location?.pathname === IMAGINE_AI_ROUTE_V2;

  // tracking_ids already handled in this session — prevents duplicate work
  // across re-polls before the server clears them.
  const processedIdsRef = useRef(new Set());
  const isProcessingRef = useRef(false);
  const intervalRef = useRef(null);
  // Keep the latest user in a ref so the polling callback always reads the
  // current value without re-binding the interval.
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const processJobs = async () => {
    if (!currentUserRef.current) return;
    if (isProcessingRef.current) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    isProcessingRef.current = true;

    try {
      const response = await getUploadJobStatus();

      if (
        !response?.status ||
        !Array.isArray(response?.data) ||
        response.data.length === 0
      ) {
        return;
      }

      const pendingItems = [];
      const failedTrackingIds = [];

      response.data.forEach((item) => {
        // Need either an s3_url to upload or a "failed" status to bypass.
        if (!item?.s3_url && !isFailedUploadStatus(item?.status)) return;

        // Skip items already handled in this session.
        if (processedIdsRef.current.has(item.tracking_id)) return;

        if (isFailedUploadStatus(item?.status)) {
          failedTrackingIds.push(item.tracking_id);
          processedIdsRef.current.add(item.tracking_id);
          return;
        }

        pendingItems.push(item);
      });

      // Bypass failed items via DELETE before doing any work.
      if (failedTrackingIds.length > 0) {
        await bypassFailedUploadEntries(failedTrackingIds);
      }

      if (pendingItems.length === 0) return;

      const successfulTrackingIds = [];

      // Group items by job_id so "link" items can reference the output URLs
      // produced by the "output" item in the same group.
      const jobGroups = new Map();
      pendingItems.forEach((item) => {
        if (!jobGroups.has(item.job_id)) jobGroups.set(item.job_id, []);
        jobGroups.get(item.job_id).push(item);
      });

      const jobOutputUrls = new Map();

      for (const [, items] of jobGroups) {
        const outputItems = items.filter((i) => i.payload_type === 'output');
        const linkItems = items.filter((i) => i.payload_type === 'link');

        // ── "output" items ────────────────────────────────────────────────
        for (const item of outputItems) {
          try {
            const toolType = String(item.payload?.tool_type || '');

            // Product Swap is bypassed — server already has the output.
            if (toolType === PRODUCT_SWAP_TOOL_TYPE) {
              processedIdsRef.current.add(item.tracking_id);
              successfulTrackingIds.push(item.tracking_id);
              continue;
            }

            const inputS3URL = item.payload.input_s3_url
              ? String(item.payload.input_s3_url)
              : '';
            // bulk-create doesn't accept this key — it is consumed locally.
            delete item.payload.input_s3_url;

            // Video outputs are stored as .mp4 — rewrite the name and key.
            if (
              toolType === 'imagineaiv2_videogen' &&
              typeof item.payload.name === 'string'
            ) {
              item.payload.name = item.payload.name.replace(/\.png$/i, '.mp4');
              if (typeof item.payload.output_image_url_key === 'string') {
                item.payload.output_image_url_key =
                  item.payload.output_image_url_key.replace(/\.png$/i, '.mp4');
              }
            }

            // 3D outputs can be `.glb` OR `.fbx` — derive the actual
            // extension from the s3_url and sync name + output_image_url_key
            // so the bulk-create payload matches what S3 actually has.
            if (toolType === 'imagineaiv2_3d') {
              const ext3D = get3DModelExtension(item.s3_url);
              if (typeof item.payload.name === 'string') {
                item.payload.name = replaceFileExtension(item.payload.name, ext3D);
              }
              if (typeof item.payload.output_image_url_key === 'string') {
                item.payload.output_image_url_key = replaceFileExtension(
                  item.payload.output_image_url_key,
                  ext3D
                );
              }
            }

            if (!item.payload.thumb_output_image_url_key) {
              item.payload.thumb_output_image_url_key = replaceFileExtension(
                item.payload.output_image_url_key,
                'png'
              );
            }

            const formData = buildBackgroundUploadFormData(item.payload);
            const bulkResponse = await postBulkCreateImagineAsset(formData);

            if (
              bulkResponse?.status &&
              Array.isArray(bulkResponse?.data) &&
              bulkResponse.data.length > 0
            ) {
              const presignedData = bulkResponse.data[0];

              let outputImageUrlKey = String(
                presignedData?.output_image_presigned_data?.fields?.key || ''
              );
              const thumbOutputImageUrlKey = String(
                presignedData?.thumb_presigned_output_data?.fields?.key || ''
              );

              const correctExt = getFileExtensionForToolType(toolType, item.s3_url);
              if (correctExt) {
                outputImageUrlKey = replaceFileExtension(
                  outputImageUrlKey,
                  correctExt
                );
              }

              jobOutputUrls.set(item.job_id, {
                outputImageUrlKey,
                thumbOutputImageUrlKey,
                inputS3Url: inputS3URL || '',
                name: String(item.payload?.name || ''),
              });

              const uploads = [];

              if (presignedData?.output_image_presigned_data && item.s3_url) {
                uploads.push(
                  fetchImageUrlAsBlob(item.s3_url).then((blob) =>
                    postFileToPresignedData(
                      blob,
                      presignedData.output_image_presigned_data
                    )
                  )
                );
              }

              // For video / 3D outputs, the thumbnail should come from the
              // input image (the output isn't a still image).
              const thumbnailSource = !correctExt
                ? item.s3_url || inputS3URL || item.input_s3_url
                : inputS3URL || item.input_s3_url || item.s3_url;

              if (presignedData?.thumb_presigned_output_data && thumbnailSource) {
                uploads.push(
                  fetchImageUrlAsBlob(thumbnailSource).then((thumb) =>
                    postFileToPresignedData(
                      thumb,
                      presignedData.thumb_presigned_output_data
                    )
                  )
                );
              }

              await Promise.all(uploads);
              triggerUpdateOutputCurator(presignedData?.id);
            }

            processedIdsRef.current.add(item.tracking_id);
            successfulTrackingIds.push(item.tracking_id);
          } catch (error) {
            // Upload attempted — notify the server via POST (not DELETE) so
            // the queue clears even when the S3 upload step fails.
            processedIdsRef.current.add(item.tracking_id);
            successfulTrackingIds.push(item.tracking_id);
            // eslint-disable-next-line no-console
            console.error('[BackgroundUpload] output item failed', {
              trackingId: item.tracking_id,
              error,
            });
          }
        }

        // ── "link" items ──────────────────────────────────────────────────
        for (const item of linkItems) {
          try {
            const toolType = String(item.payload?.tool_type || '');
            if (toolType === PRODUCT_SWAP_TOOL_TYPE) {
              processedIdsRef.current.add(item.tracking_id);
              successfulTrackingIds.push(item.tracking_id);
              continue;
            }

            const outputUrls = jobOutputUrls.get(item.job_id);
            if (outputUrls) {
              item.payload.manual_output_image_path = outputUrls.outputImageUrlKey;
              item.payload.manual_thumb_output_image_path =
                outputUrls.thumbOutputImageUrlKey || outputUrls.inputS3Url || '';
              if (outputUrls.name) {
                item.payload.name = outputUrls.name;
              }
            }

            delete item.payload.input_s3_url;

            const formData = buildBackgroundUploadFormData(item.payload);
            await postBulkCreateImagineAsset(formData);

            processedIdsRef.current.add(item.tracking_id);
            successfulTrackingIds.push(item.tracking_id);
          } catch (error) {
            processedIdsRef.current.add(item.tracking_id);
            successfulTrackingIds.push(item.tracking_id);
            // eslint-disable-next-line no-console
            console.error('[BackgroundUpload] link item failed', {
              trackingId: item.tracking_id,
              error,
            });
          }
        }
      }

      if (successfulTrackingIds.length > 0) {
        await notifyFilesUploaded(successfulTrackingIds);
      }
    } catch {
      // Silent failure — surface nothing in the UI.
    } finally {
      isProcessingRef.current = false;
    }
  };

  useEffect(() => {
    if (!currentUser) {
      // if (intervalRef.current) {
      //   clearInterval(intervalRef.current);
      //   intervalRef.current = null;
      // }
      return undefined;
    }
    if (!isImagineAiRoute) return undefined;
    processJobs();
  }, [JSON?.stringify(currentUser?.id), isImagineAiRoute]);

  useEffect(() => {
    if (!triggerBackgroundUploadProcess) return;
    if (!currentUser) {
      dispatch(setTriggerBackgroundUploadProcess(false));
      return;
    }
    (async () => {
      await processJobs();
      dispatch(setTriggerBackgroundUploadProcess(false));
    })();
  }, [triggerBackgroundUploadProcess]);

  return null;
}
